/**
 * In-App settings (tenant-wide) — a Modal form backed by RRS `GET/PUT /in-app-settings`:
 *  - D7 global cooldown: after showing any in-app message a phone shows no other for N seconds
 *    (default 30; 0 = off). Saved with the Save button (PUT).
 *  - D13 test devices: a registered device bypasses frequency caps, daily caps and the cooldown, and
 *    tags its events `test=true`. Added / removed immediately (POST / DELETE …/test-devices).
 *    Picked from the ids that recently synced (GET …/recent-devices) or typed by hand.
 *  - DA-BUG-1053 brand colour for dark appearance (`brand_color_dark`, or null = the brand colour).
 *  - B-14 brand colour (`brand_color`, #RRGGBB or null = default #2563eb), saved in the same PUT.
 *  - DA-BUG-1132 global display cap (`global_max_per_session`, `global_max_per_day`; null = off).
 *  - P-8 allowed deep-link schemes (`deep_link_schemes`), saved in the same PUT.
 *  - IAM-094 form-API domains: hostnames a form block's API action may POST to (empty = all blocked).
 *    Saved with the cooldown in ONE PUT, so saving one never wipes the other.
 * Contract: RRS docs/in-app-sdk-payload.md "Tenant settings in every sync".
 */
import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, Input, Text, FormControl, FormLabel, FormHelperText, FormErrorMessage, InputGroup,
  InputRightAddon, Box, Flex, IconButton, Spinner, Center, Divider, useToast,
} from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import axios from 'axios';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import { validateDeviceId, settingsFromApi, recentDeviceOptions, MAX_TEST_DEVICES, DEFAULT_BRAND_COLOR, addDeepLinkScheme, MAX_DEEP_LINK_SCHEMES } from './inAppSettingsModel';
import { addFormApiDomain, buildSettingsPut, MAX_FORM_API_DOMAINS } from './formApiDomains';

const BASE = () => `${process.env.REACT_APP_API_URL}/in-app-settings`;
const headers = getApiHeaders;
const msgOf = (e) => e?.response?.data?.message || e.message;

export default function InAppSettingsModal({ isOpen, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [cooldown, setCooldown] = useState('30');
  const [cooldownErr, setCooldownErr] = useState(null);
  const [devices, setDevices] = useState([]);
  const [newId, setNewId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [deviceErr, setDeviceErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyDevice, setBusyDevice] = useState(null);
  const [recent, setRecent] = useState([]);
  const [domains, setDomains] = useState([]);
  const [domainsKnown, setDomainsKnown] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [domainErr, setDomainErr] = useState(null);
  const [brand, setBrand] = useState('');
  const [brandKnown, setBrandKnown] = useState(false);
  const [brandErr, setBrandErr] = useState(null);
  const [brandDark, setBrandDark] = useState('');
  const [brandDarkKnown, setBrandDarkKnown] = useState(false);
  const [brandDarkErr, setBrandDarkErr] = useState(null);
  const [capSession, setCapSession] = useState('');
  const [capDay, setCapDay] = useState('');
  const [capKnown, setCapKnown] = useState(false);
  const [capErr, setCapErr] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [schemesKnown, setSchemesKnown] = useState(false);
  const [newScheme, setNewScheme] = useState('');
  const [schemeErr, setSchemeErr] = useState(null);

  const apply = (data) => { const s = settingsFromApi(data); setDevices(s.devices); return s; };

  useEffect(() => {
    if (!isOpen) return undefined;
    let off = false;
    setLoading(true); setLoadError(null); setCooldownErr(null); setDeviceErr(null); setNewId(''); setNewLabel(''); setNewDomain(''); setDomainErr(null);
    axios.get(BASE(), { headers: headers() })
      .then(({ data }) => { if (!off) { const s = apply(data?.data); setCooldown(String(s.cooldown)); setDomains(s.domains); setDomainsKnown(s.domainsKnown); setBrand(s.brandColor); setBrandKnown(s.brandKnown); setBrandErr(null); setBrandDark(s.brandDark); setBrandDarkKnown(s.brandDarkKnown); setBrandDarkErr(null); setCapSession(s.capSession); setCapDay(s.capDay); setCapKnown(s.capKnown); setCapErr(null); setSchemes(s.schemes); setSchemesKnown(s.schemesKnown); setNewScheme(''); setSchemeErr(null); } })
      .catch((e) => { if (!off) setLoadError(msgOf(e)); })
      .finally(() => { if (!off) setLoading(false); });
    // Recent device ids (D13 picker). Optional: an older backend has no such route → no list.
    axios.get(`${BASE()}/recent-devices`, { params: { limit: 20 }, headers: headers() })
      .then(({ data }) => { if (!off) setRecent(data?.data?.devices || []); })
      .catch(() => { if (!off) setRecent([]); });
    return () => { off = true; };
  }, [isOpen]);

  const save = async () => {
    const { body, errors } = buildSettingsPut(cooldown, domains, domainsKnown, { brandColor: brand, brandKnown, brandDark, brandDarkKnown, schemes, schemesKnown, capSession, capDay, capKnown });
    if (!body) { setCooldownErr(errors.cooldown || null); setDomainErr(errors.domains || null); setBrandErr(errors.brand || null); setBrandDarkErr(errors.brandDark || null); setCapErr(errors.cap || null); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(BASE(), body, { headers: headers() });
      const s = apply(data?.data); setCooldown(String(s.cooldown)); setDomains(s.domains);
      toast({ title: 'In-app settings saved', description: 'Phones pick this up on their next sync.', status: 'success' });
      onClose();
    } catch (e) {
      // Each server error goes next to its own field (errors.form_api_domains / the cooldown message).
      const fe = e?.response?.data?.errors || {};
      if (fe.form_api_domains) setDomainErr(fe.form_api_domains); else if (fe.brand_color) setBrandErr(fe.brand_color); else if (fe.brand_color_dark) setBrandDarkErr(fe.brand_color_dark); else if (fe.deep_link_schemes) setSchemeErr(fe.deep_link_schemes); else if (fe.global_max_per_session || fe.global_max_per_day) setCapErr(fe.global_max_per_session || fe.global_max_per_day); else setCooldownErr(msgOf(e));
    } finally { setSaving(false); }
  };

  const addScheme = () => {
    const r = addDeepLinkScheme(schemes, newScheme);
    if (r.error) { setSchemeErr(r.error); return; }
    setSchemes(r.schemes); setNewScheme(''); setSchemeErr(null);
  };

  const addDomain = () => {
    const r = addFormApiDomain(domains, newDomain);
    if (r.error) { setDomainErr(r.error); return; }
    setDomains(r.domains); setNewDomain(''); setDomainErr(null);
  };

  const addDevice = async (picked) => {
    const r = validateDeviceId(typeof picked === 'string' ? picked : newId, devices);
    if (r.error) { setDeviceErr(r.error); return; }
    setBusyDevice(typeof picked === 'string' ? `__pick:${picked}` : '__add');
    try {
      const { data } = await axios.post(`${BASE()}/test-devices`, { device_id: r.value, label: newLabel.trim() || undefined }, { headers: headers() });
      apply(data?.data); if (typeof picked !== 'string') { setNewId(''); setNewLabel(''); } setDeviceErr(null);
    } catch (e) { setDeviceErr(msgOf(e)); } finally { setBusyDevice(null); }
  };

  const removeDevice = async (id) => {
    setBusyDevice(id);
    try {
      const { data } = await axios.delete(`${BASE()}/test-devices/${encodeURIComponent(id)}`, { headers: headers() });
      apply(data?.data);
    } catch (e) { toast({ title: 'Could not remove the device', description: msgOf(e), status: 'error' }); } finally { setBusyDevice(null); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="14px" data-testid="in-app-settings-modal">
        <ModalHeader fontSize="17px">In-app settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? <Center py={10}><Spinner /></Center> : loadError ? (
            <Text color="red.500" fontSize="sm">Could not load settings: {loadError}</Text>
          ) : (
            <>
              <FormControl isInvalid={!!cooldownErr}>
                <FormLabel fontSize="sm" mb={1}>Time between in-app messages</FormLabel>
                <InputGroup size="sm" maxW="220px">
                  <Input type="number" min={0} max={86400} value={cooldown} aria-label="Global cooldown in seconds"
                    onChange={(e) => { setCooldown(e.target.value); setCooldownErr(null); }} />
                  <InputRightAddon>seconds</InputRightAddon>
                </InputGroup>
                {cooldownErr ? <FormErrorMessage>{cooldownErr}</FormErrorMessage> : (
                  <FormHelperText fontSize="xs">After a phone shows any in-app message, it waits this long before showing another. Each message's own frequency rules still apply. 0 turns it off. Default 30.</FormHelperText>
                )}
              </FormControl>

              {capKnown && (
                <FormControl isInvalid={!!capErr} mt={5}>
                  <FormLabel fontSize="sm" mb={1}>Most in-app messages a customer sees</FormLabel>
                  <Flex gap={3} wrap="wrap">
                    <InputGroup size="sm" maxW="200px">
                      <Input type="number" min={1} max={1000} placeholder="No cap" aria-label="Global max per session" value={capSession}
                        onChange={(e) => { setCapSession(e.target.value); setCapErr(null); }} />
                      <InputRightAddon>per session</InputRightAddon>
                    </InputGroup>
                    <InputGroup size="sm" maxW="200px">
                      <Input type="number" min={1} max={1000} placeholder="No cap" aria-label="Global max per day" value={capDay}
                        onChange={(e) => { setCapDay(e.target.value); setCapErr(null); }} />
                      <InputRightAddon>per day</InputRightAddon>
                    </InputGroup>
                  </Flex>
                  {capErr ? <FormErrorMessage data-testid="cap-error">{capErr}</FormErrorMessage> : (
                    <FormHelperText fontSize="xs">Counts every in-app message together (per app session, and per day in the phone's time). Messages marked "Exempt from the global cap" neither count nor are blocked. Empty = no cap.</FormHelperText>
                  )}
                </FormControl>
              )}

              {brandKnown && (
                <>
                  <Divider my={5} />
                  <FormControl isInvalid={!!brandErr}>
                    <FormLabel fontSize="sm" mb={1}>Brand colour</FormLabel>
                    <Flex gap={2} align="center" maxW="260px">
                      <Input type="color" size="sm" w="44px" p={0.5} aria-label="Brand colour picker"
                        value={/^#[0-9a-fA-F]{6}$/.test(brand.trim()) ? brand.trim() : DEFAULT_BRAND_COLOR}
                        onChange={(e) => { setBrand(e.target.value.toUpperCase()); setBrandErr(null); }} />
                      <Input size="sm" placeholder={DEFAULT_BRAND_COLOR} aria-label="Brand colour hex" value={brand}
                        onChange={(e) => { setBrand(e.target.value); setBrandErr(null); }} />
                    </Flex>
                    {brandErr ? <FormErrorMessage data-testid="brand-error">{brandErr}</FormErrorMessage> : (
                      <FormHelperText fontSize="xs">Used wherever a message uses the brand colour, and when a colour can't be read on the phone. #RRGGBB; empty = the default ({DEFAULT_BRAND_COLOR}). Saved with the Save button.</FormHelperText>
                    )}
                  </FormControl>
                </>
              )}

              {brandDarkKnown && (
                <FormControl isInvalid={!!brandDarkErr} mt={brandKnown ? 3 : 5}>
                  <FormLabel fontSize="sm" mb={1}>Brand colour (dark)</FormLabel>
                  <Flex gap={2} align="center" maxW="260px">
                    <Input type="color" size="sm" w="44px" p={0.5} aria-label="Brand colour (dark) picker"
                      value={/^#[0-9a-fA-F]{6}$/.test(brandDark.trim()) ? brandDark.trim() : '#1C1C1E'}
                      onChange={(e) => { setBrandDark(e.target.value.toUpperCase()); setBrandDarkErr(null); }} />
                    <Input size="sm" placeholder="Same as brand colour" aria-label="Brand colour (dark) hex" value={brandDark}
                      onChange={(e) => { setBrandDark(e.target.value); setBrandDarkErr(null); }} />
                  </Flex>
                  {brandDarkErr ? <FormErrorMessage data-testid="brand-dark-error">{brandDarkErr}</FormErrorMessage> : (
                    <FormHelperText fontSize="xs">Used instead of the brand colour when a message shows in dark appearance. #RRGGBB; empty = the brand colour.</FormHelperText>
                  )}
                </FormControl>
              )}

              {schemesKnown && (
                <>
                  <Divider my={5} />
                  <FormControl isInvalid={!!schemeErr}>
                    <FormLabel fontSize="sm" mb={1}>Allowed deep-link schemes</FormLabel>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      A button's deep link may use https or one of these app schemes (e.g. myapp for myapp://…). With none listed, any custom scheme is accepted. Open-URL, fallback, image and video links are always https only. Up to {MAX_DEEP_LINK_SCHEMES}. Saved with the Save button.
                    </Text>
                    <Flex gap={2}>
                      <Input size="sm" placeholder="myapp" aria-label="Deep-link scheme" value={newScheme}
                        onChange={(e) => { setNewScheme(e.target.value); setSchemeErr(null); }} onKeyDown={(e) => { if (e.key === 'Enter') addScheme(); }} />
                      <Button size="sm" onClick={addScheme}>Add scheme</Button>
                    </Flex>
                    {schemeErr && <FormErrorMessage data-testid="scheme-error">{schemeErr}</FormErrorMessage>}
                  </FormControl>
                  <Box mt={3} borderWidth="1px" borderRadius="8px" data-testid="deep-link-scheme-list">
                    {schemes.length === 0 ? <Text fontSize="xs" color="gray.500" p={3}>None: any custom scheme is accepted.</Text> : schemes.map((d) => (
                      <Flex key={d} px={3} py={1.5} align="center" justify="space-between" borderTopWidth="1px" _first={{ borderTopWidth: 0 }}>
                        <Text fontSize="sm" fontFamily="mono">{d}</Text>
                        <IconButton size="xs" variant="ghost" aria-label={`Remove scheme ${d}`} icon={<Trash2 size={13} />} onClick={() => setSchemes(schemes.filter((x) => x !== d))} />
                      </Flex>
                    ))}
                  </Box>
                </>
              )}

              {domainsKnown && (
                <>
                  <Divider my={5} />
                  <FormControl isInvalid={!!domainErr}>
                    <FormLabel fontSize="sm" mb={1}>Form API domains</FormLabel>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      A form block can send its answers to your API only on these hosts (https only). Enter a hostname such as api.example.com, or *.example.com for its subdomains (example.com itself must be listed separately). With no domains, every form API post is blocked. Up to {MAX_FORM_API_DOMAINS}. Saved with the Save button.
                    </Text>
                    <Flex gap={2}>
                      <Input size="sm" placeholder="api.example.com" aria-label="Form API domain" value={newDomain}
                        onChange={(e) => { setNewDomain(e.target.value); setDomainErr(null); }} onKeyDown={(e) => { if (e.key === 'Enter') addDomain(); }} />
                      <Button size="sm" onClick={addDomain}>Add domain</Button>
                    </Flex>
                    {domainErr && <FormErrorMessage data-testid="domain-error">{domainErr}</FormErrorMessage>}
                  </FormControl>
                  <Box mt={3} borderWidth="1px" borderRadius="8px" data-testid="form-api-domain-list">
                    {domains.length === 0 ? <Text fontSize="xs" color="orange.600" p={3}>No domains: every form API post is blocked.</Text> : domains.map((d) => (
                      <Flex key={d} px={3} py={1.5} align="center" justify="space-between" borderTopWidth="1px" _first={{ borderTopWidth: 0 }}>
                        <Text fontSize="sm" fontFamily="mono">{d}</Text>
                        <IconButton size="xs" variant="ghost" aria-label={`Remove domain ${d}`} icon={<Trash2 size={13} />} onClick={() => setDomains(domains.filter((x) => x !== d))} />
                      </Flex>
                    ))}
                  </Box>
                </>
              )}

              <Divider my={5} />

              <Text fontSize="sm" fontWeight={600}>Test devices</Text>
              <Text fontSize="xs" color="gray.500" mt={1} mb={3}>
                A test device sees messages without frequency caps, daily caps or the wait above, and its events are tagged test=true so reports can leave them out.
                Use the identifier the app sends: its distinct id, phone, email or external id. Up to {MAX_TEST_DEVICES}.
              </Text>
              <Flex gap={2} align="flex-start">
                <FormControl isInvalid={!!deviceErr} flex="1">
                  <Input size="sm" placeholder="Device identifier" aria-label="Test device identifier" value={newId}
                    onChange={(e) => { setNewId(e.target.value); setDeviceErr(null); }} onKeyDown={(e) => { if (e.key === 'Enter') addDevice(); }} />
                  {deviceErr && <FormErrorMessage>{deviceErr}</FormErrorMessage>}
                </FormControl>
                <Input size="sm" w="150px" placeholder="Label (optional)" aria-label="Test device label" value={newLabel} maxLength={100} onChange={(e) => setNewLabel(e.target.value)} />
                <Button size="sm" onClick={() => addDevice()} isLoading={busyDevice === '__add'}>Add</Button>
              </Flex>
              {recentDeviceOptions(recent, devices).length > 0 && (
                <Box mt={3} data-testid="recent-devices">
                  <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1}>Recently synced (last 7 days)</Text>
                  <Box borderWidth="1px" borderRadius="8px" maxH="160px" overflowY="auto">
                    {recentDeviceOptions(recent, devices).map((d) => (
                      <Flex key={d.device_id} px={3} py={1.5} align="center" justify="space-between" borderTopWidth="1px" _first={{ borderTopWidth: 0 }}>
                        <Box minW={0}>
                          <Text fontSize="sm" fontFamily="mono" noOfLines={1}>{d.device_id}</Text>
                          {d.last_seen && <Text fontSize="xs" color="gray.500">Last synced {new Date(d.last_seen).toLocaleString()}</Text>}
                        </Box>
                        <Button size="xs" variant="outline" aria-label={`Add ${d.device_id} as a test device`} isLoading={busyDevice === `__pick:${d.device_id}`} onClick={() => addDevice(d.device_id)}>Add</Button>
                      </Flex>
                    ))}
                  </Box>
                </Box>
              )}
              <Box mt={3} borderWidth="1px" borderRadius="8px" data-testid="test-device-list">
                {devices.length === 0 ? <Text fontSize="xs" color="gray.500" p={3}>No test devices yet.</Text> : devices.map((d) => (
                  <Flex key={d.device_id} px={3} py={2} align="center" justify="space-between" borderTopWidth="1px" _first={{ borderTopWidth: 0 }}>
                    <Box minW={0}>
                      <Text fontSize="sm" fontFamily="mono" noOfLines={1}>{d.device_id}</Text>
                      {d.label && <Text fontSize="xs" color="gray.500">{d.label}</Text>}
                    </Box>
                    <IconButton size="xs" variant="ghost" aria-label={`Remove ${d.device_id}`} icon={<Trash2 size={13} />} isLoading={busyDevice === d.device_id} onClick={() => removeDevice(d.device_id)} />
                  </Flex>
                ))}
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving} isDisabled={loading || !!loadError}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
